import { noop } from './noop'

interface IOptions {
  readonly [index: string]: any

  debug?: boolean
  events?: string[]
  grace_period?: number
  timeout_period?: number
  name?: string
  onGracePeriodTimeout?: () => void
  onLocalTimerReset?: () => void
  onReset?: () => void
  onSharedTimerReset?: () => void
  onTimeout?: () => void
}
const callbacks = Object.freeze([
  'onGracePeriodTimeout',
  'onLocalTimerReset',
  'onReset',
  'onSharedTimerReset',
  'onTimeout',
])

class SharedTimeout {
  private boundResetSharedTimer: () => void
  private gracePeriodTimer: number
  private lastActivity: Date
  private localTimer: number
  private options: Readonly<IOptions>
  private sharedInterval: number

  constructor({
    debug = false,
    events = ['click', 'keypress', 'mousemove'],
    grace_period = 60 * 2, // two minutes
    name = 'SharedTimeout',
    onGracePeriodTimeout = noop,
    onLocalTimerReset = noop,
    onReset = noop,
    onSharedTimerReset = noop,
    onTimeout = noop,
    timeout_period = 60, // one minute
  }: IOptions) {
    this.options = Object.freeze({
      debug,
      events,
      grace_period,
      name,
      onGracePeriodTimeout,
      onLocalTimerReset,
      onReset,
      onSharedTimerReset,
      onTimeout,
      timeout_period,
    })
    callbacks.forEach((callback) => this.options[callback].bind(this))

    this.reset()
  }

  get sharedActivityKey() {
    return `${this.options.name}-SharedTimeout-lastReset`
  }

  get localActivityValue() {
    return this.lastActivity.getTime()
  }

  get sharedActivityValue() {
    const value = window.localStorage.getItem(this.sharedActivityKey)
    return parseInt(value, 10)
  }

  set sharedActivityValue(value) {
    window.localStorage.setItem(this.sharedActivityKey, value.toString())
  }

  public destroy() {
    window.clearInterval(this.sharedInterval)
    window.clearTimeout(this.gracePeriodTimer)
    window.clearTimeout(this.localTimer)

    this.clearListeners()
  }

  protected log(message: string) {
    if (!this.options.debug) {
      return
    }

    window.console.debug(`SharedTimeout ${this.options.name}: ${message}`)
  }

  protected clearListeners() {
    this.options.events.forEach((event) => {
      document.removeEventListener(event, this.boundResetSharedTimer)
    })
  }

  private reset() {
    this.log('reset')

    this.boundResetSharedTimer = this.resetSharedTimer.bind(this)

    this.resetLocalTimer()
    window.clearTimeout(this.gracePeriodTimer)
    this.resetSharedTimer()

    window.clearInterval(this.sharedInterval)
    this.sharedInterval = window.setInterval(
      this.checkSharedTimer.bind(this),
      1000
    )

    this.options.events.forEach((event) => {
      document.addEventListener(event, this.boundResetSharedTimer)
    })

    this.options.onReset.call(this)
  }

  private resetLocalTimer() {
    this.log('resetLocalTimer')

    this.options.onLocalTimerReset.call(this)

    window.clearTimeout(this.localTimer)
    this.localTimer = window.setTimeout(
      this.timeout.bind(this),
      this.options.timeout_period * 1000
    )
    this.lastActivity = new Date()
  }

  private resetSharedTimer() {
    this.log('resetSharedTimer')

    this.resetLocalTimer()

    // Round to the nearest 0.5 second to smooth over timing jitter.
    const sanitizedLastActivity =
      this.localActivityValue - (this.localActivityValue % 500)
    this.sharedActivityValue = sanitizedLastActivity
  }

  private timeout() {
    this.log('timeout')

    this.options.onTimeout.call(this)

    window.clearTimeout(this.gracePeriodTimer)
    this.gracePeriodTimer = window.setTimeout(
      this.gracePeriodTimeout.bind(this),
      this.options.grace_period * 1000
    )
  }

  private gracePeriodTimeout() {
    this.log('gracePeriodTimeout')

    this.options.onGracePeriodTimeout.call(this)
  }

  private checkSharedTimer() {
    this.log(
      `checkSharedTimer: local(${this.localActivityValue}), shared (${this.sharedActivityValue})`
    )

    if (this.sharedActivityValue > this.localActivityValue) {
      this.resetLocalTimer()
      this.options.onSharedTimerReset.call(this)
    }
  }
}

export { SharedTimeout }
